import {Dialog} from '@reach/dialog';
import {DialogFooter, DialogTitle} from '@common/components/Dialog';
import {
  ErrorMessage,
  FormGroup,
  FormItemGroup,
  Input,
  RequireMarker,
  TimesInput,
  Checkbox,
} from '@common/components/form-items';
import {Controller, useFormContext} from 'react-hook-form';
import Button, {ButtonGroup} from '@common/components/Button';
import Select from '@common/components/Select';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSquare} from '@fortawesome/free-solid-svg-icons';
import {ShiftSchedule, Cycles, CycleType, cycleTypeOptions, WorkType} from '@gakki/api/types';
import RestTime from './rest-time';
import {Interval} from 'luxon';
import {isNilOrEmpty} from '@gakki/utils/validate';
import {timeToDateTime} from '@gakki/utils/time';
import StartDayTimeInfo from '@gakki/components/info-tip/start-day-time-info';
import {DevTool} from '@hookform/devtools';
import { useEffect } from 'react';

const colors = [
  '#FE9C4E',
  '#FB7E1A',
  '#E75718',
  '#9A3A0F',
  '#522917',
  '#92C31E',
  '#679102',
  '#06A519',
  '#056502',
  '#415119',
  '#07C1B6',
  '#054F52',
  '#084F92',
  '#2B2BC9',
  '#222240',
  '#6531DD',
  '#8B27F1',
  '#300592',
  '#D81FE9',
  '#F341A1',
];

const colorOptions = colors.map(c => ({value: c, label: c}));
const weekdays = [
  {
    label: '星期日',
    name: 'sunday',
  },
  {
    label: '星期一',
    name: 'monday',
  },
  {
    label: '星期二',
    name: 'tuesday',
  },
  {
    label: '星期三',
    name: 'wednesday',
  },
  {
    label: '星期四',
    name: 'thursday',
  },
  {
    label: '星期五',
    name: 'friday',
  },
  {
    label: '星期六',
    name: 'saturday',
  },
]

type ShiftScheduleFormProps = {
  isOpen?: boolean;
  defaultValues?: ShiftSchedule;
  onClose: () => void;
  onConfirm: (data: ShiftSchedule) => void;
  triggerValidate?: boolean;
};

function ShiftScheduleForm(props: ShiftScheduleFormProps) {
  const {isOpen, onClose, onConfirm, triggerValidate = false} = props;

  const {
    register,
    handleSubmit,
    formState: {errors, isSubmitted},
    control,
    getValues,
    setValue,
    watch,
    trigger,
  } = useFormContext<ShiftSchedule>();
  const id = watch('id');
  const cycles = watch('cycles');
  const cycleType = watch('cycleType');

  useEffect(() => {
    if (triggerValidate) {
      setTimeout(() => { // trigger時欄位未註冊，先用setTimeout延遲
        trigger()
      }, 1)
    }
  }, [triggerValidate, trigger])

  const getUnselectedWeekday = (workType: WorkType) => {
    return weekdays
    .filter((day) => (
      cycles && typeof cycles[day.name as keyof Cycles] !== 'number'
    ))
    .map((day) => ({ ...day, value: workType }))
  }

  const clearSelectedWeekday = (workType: WorkType) => {
    weekdays.forEach((day) => {
      if (cycles[day.name as keyof Cycles] === workType) {
        setValue(`cycles.${day.name}`, undefined)
      }
    })
  }

  const getSelectedWeekday = (workType: WorkType) => {
    return weekdays.filter((day) => cycles && cycles[day.name as keyof Cycles] === workType)
  }
  return (
    <Dialog isOpen={isOpen} style={{width: 650}} aria-label="shift-schedule-dialog">
      <DialogTitle title={`${id ? '編輯' : '新增'}班次`} onClose={onClose} />
      <form
        className={'shift-schedule-form'}
        onSubmit={handleSubmit(d => {
          onConfirm(d);
        })}
      >
        <div
          className={
            'flex flex-col border-b border-b-[#e9eced] mb-5 pt-[18px] pb-10'
          }
        >
          <input type={'hidden'} ref={register} name={'id'} />
          <FormGroup columnTemplates={1}>
            <FormItemGroup>
              <label htmlFor={'name'}>
                班次名稱
                <RequireMarker />
              </label>
              <Input
                data-test-id={'name'}
                name={'name'}
                id={'name'}
                placeholder={'請輸入班次名稱'}
                autoFocus
                maxLength={10}
                error={errors.name?.message}
                ref={register({
                  required: '班次名稱為必填',
                })}
              />
              <ErrorMessage>{errors.name?.message}</ErrorMessage>
            </FormItemGroup>
          </FormGroup>
          <FormGroup columnTemplates={2} data-test-id={'color'}>
            <FormItemGroup>
              <label htmlFor={'color'}>
                班次顏色
                <RequireMarker />
              </label>
              <Controller
                control={control}
                render={({onChange, value}) => (
                  <Select
                    name={'color'}
                    id={'color'}
                    placeholder={'請選擇班次顏色'}
                    options={colorOptions}
                    error={errors.color}
                    onChange={(option: {value: string}) =>
                      onChange(option.value)
                    }
                    formatOptionLabel={({label}: {label: string}) => (
                      <div>
                        <FontAwesomeIcon
                          icon={faSquare}
                          className={'mr-[5px] text-lg'}
                          style={{
                            color: label,
                          }}
                        />
                      </div>
                    )}
                    value={value ? {value: value, label: value} : null}
                  />
                )}
                name="color"
                rules={{
                  required: '班次顏色為必填',
                }}
              />
            </FormItemGroup>
            <FormItemGroup data-test-id={'day-start-time'}>
              <label htmlFor={'dayStartTime'}>
                日切換時間
                <StartDayTimeInfo />
                <RequireMarker />
              </label>
              <Controller
                control={control}
                render={({onChange, value}) => (
                  <Select
                    id={'dayStartTime'}
                    placeholder={'請選擇日切換時間'}
                    options={new Array(24).fill(1).map((v, idx) => ({
                      value: `${idx.toString().padStart(2, '0')}:00`,
                      label: `${idx.toString().padStart(2, '0')}:00`,
                    }))}
                    error={errors.dayStartTime}
                    onChange={(option: {value: string}) => {
                      onChange(option.value);
                    }}
                    value={value ? {value: value, label: value} : null}
                  />
                )}
                name="dayStartTime"
                rules={{
                  required: '日切換時間為必填',
                  validate: dayStartTime => {
                    const {
                      startWorkingTime: workOnTime,
                      offWorkingTime: workOffTime,
                    } = getValues();
                    if (
                      isNilOrEmpty(workOffTime) ||
                      isNilOrEmpty(workOnTime) ||
                      isNilOrEmpty(dayStartTime)
                    ) {
                      return true;
                    }
                    const start = timeToDateTime(workOnTime);
                    const end = timeToDateTime(workOffTime, start);
                    const baseLine = timeToDateTime(dayStartTime, start);

                    return Interval.fromDateTimes(start, end).contains(baseLine)
                      ? '上班時間區間不可超過日切換時間'
                      : true;
                  },
                }}
              />
            </FormItemGroup>
          </FormGroup>
          <FormGroup columnTemplates={2} data-test-id={'start-working-time'}>
            <FormItemGroup>
              <label htmlFor={'startWorkingTime'}>
                上班時間
                <RequireMarker />
              </label>
              <div className='w-[280px]'>
                <Controller
                  control={control}
                  render={({onChange, value}) => (
                    <TimesInput
                      onChange={onChange}
                      value={value}
                      error={errors?.startWorkingTime?.message}
                    />
                  )}
                  name="startWorkingTime"
                  rules={{required: '上班時間為必填'}}
                />
              </div>
            </FormItemGroup>
            <FormItemGroup id={'off-working-time'}>
              <label htmlFor={'offWorkingTime'}>
                下班時間
                <RequireMarker />
              </label>
              <div className='w-[280px]'>
                <Controller
                  control={control}
                  render={({onChange, value}) => (
                    <TimesInput
                      onChange={onChange}
                      value={value}
                      error={errors?.offWorkingTime?.message}
                    />
                  )}
                  name="offWorkingTime"
                  rules={{required: '下班時間為必填'}}
                />
              </div>
            </FormItemGroup>
          </FormGroup>
          <RestTime />
          <FormGroup columnTemplates={2} data-test-id={'cycle-type'}>
            <FormItemGroup>
              <label htmlFor={'cycle-type'}>
                設定重複時間
                <RequireMarker />
              </label>
              <Controller
                control={control}
                render={({onChange, value}) => (
                  <Select
                    name={'cycle-type'}
                    id={'cycle-type'}
                    placeholder={'請選擇重複時間'}
                    options={cycleTypeOptions}
                    error={errors.cycleType}
                    onChange={(option: {value: CycleType}) => {
                      onChange(option.value)
                      if (option.value === CycleType.weekly && cycles.monday === undefined) {
                        weekdays.forEach((day) => {
                          switch (day.name) {
                            case 'sunday':
                              setValue(`cycles.${day.name}`, WorkType.vacation)
                              break;
                            case 'saturday':
                              setValue(`cycles.${day.name}`, WorkType.dayOff)
                              break;
                            default:
                              setValue(`cycles.${day.name}`, WorkType.work)
                              break;
                          }
                        })
                      }
                    }}
                    value={cycleTypeOptions.find((o) => o.value === value)}
                  />
                )}
                name="cycleType"
                rules={{required: '重複時間為必填'}}
              />
            </FormItemGroup>
          </FormGroup>
          <div style={{ display: cycleType === CycleType.weekly ? 'block' : 'none'}}>
            <FormGroup columnTemplates={1} data-test-id={'cycles'}>
              <FormItemGroup>
                <label htmlFor={'cycles'}>
                  重複於每週的：
                </label>
                <Controller
                  control={control}
                  name={`forCyclesError`}
                  render={() => (
                    <div className="flex">
                      {
                        weekdays.map((day) => (
                          <Controller
                            key={day.name}
                            control={control}
                            name={`cycles.${day.name}`}
                            render={({onChange, value}) => (
                              <Checkbox
                                margin="0 18px 0 0"
                                labelName={day.label}
                                checked={value === WorkType.work}
                                onChange={e => {
                                  onChange(e.target.checked ? WorkType.work : undefined)
                                  if (isSubmitted) {
                                    trigger('forCyclesError')
                                  }
                                }}
                                disabled={value > WorkType.work}
                              />
                            )}
                          />
                        ))
                      }
                    </div>
                  )}
                  rules={{
                    validate: () => {
                      // 後端使用 -1 取代 null
                      return watch('cycleType') === CycleType.weekly ? !weekdays.some(day => typeof watch('cycles')[day.name as keyof Cycles] !== 'number' || Number(watch('cycles')[day.name as keyof Cycles]) < 0) || "請確認一週內的每一天都有被設定到 (上班日/休息日/例假日/休假 擇一)" : true
                    }
                  }}
                />
                <ErrorMessage>{(errors as any).forCyclesError?.message}</ErrorMessage>
              </FormItemGroup>
            </FormGroup>
            <FormGroup columnTemplates={2}>
              <FormItemGroup>
                <label htmlFor={'cycles-day-off'}>
                  休息日
                </label>
                <Select
                  isMulti
                  id={'cycles-day-off'}
                  placeholder={'請選擇休息日'}
                  options={getUnselectedWeekday(WorkType.dayOff)}
                  onChange={(options: any[], params: any) => {
                    if (params.action === 'select-option') {
                      options.forEach((day) => setValue(`cycles.${day.name}`, WorkType.dayOff))
                    }
                    if (params.action === 'remove-value') {
                      setValue(`cycles.${params.removedValue.name}`, undefined)
                    }
                    if (params.action === 'clear') {
                      clearSelectedWeekday(WorkType.dayOff)
                    }
                    if (isSubmitted) {
                      trigger('forCyclesError')
                    }
                  }}
                  value={getSelectedWeekday(WorkType.dayOff)}
                />
              </FormItemGroup>
              <FormItemGroup>
                <label htmlFor={'cycles-weekend'}>
                  例假日
                </label>
                <Select
                  isMulti
                  id={'cycles-weekend'}
                  placeholder={'請選擇例假日'}
                  options={getUnselectedWeekday(WorkType.vacation)}
                  onChange={(options: any[], params: any) => {
                    if (params.action === 'select-option') {
                      options.forEach((day) => setValue(`cycles.${day.name}`, WorkType.vacation))
                    }
                    if (params.action === 'remove-value') {
                      setValue(`cycles.${params.removedValue.name}`, undefined)
                    }
                    if (params.action === 'clear') {
                      clearSelectedWeekday(WorkType.vacation)
                    }
                    if (isSubmitted) {
                      trigger('forCyclesError')
                    }
                  }}
                  value={getSelectedWeekday(WorkType.vacation)}
                />
              </FormItemGroup>
              <FormItemGroup>
                <label htmlFor={'cycles-vacation'}>
                  休假
                </label>
                <Select
                  isMulti
                  id={'cycles-vacation'}
                  placeholder={'請選擇休假'}
                  options={getUnselectedWeekday(WorkType.weekend)}
                  onChange={(options: any[], params: any) => {
                    if (params.action === 'select-option') {
                      options.forEach((day) => setValue(`cycles.${day.name}`, WorkType.weekend))
                    }
                    if (params.action === 'remove-value') {
                      setValue(`cycles.${params.removedValue.name}`, undefined)
                    }
                    if (params.action === 'clear') {
                      clearSelectedWeekday(WorkType.weekend)
                    }
                    if (isSubmitted) {
                      trigger('forCyclesError')
                    }
                  }}
                  value={getSelectedWeekday(WorkType.weekend)}
                />
              </FormItemGroup>
            </FormGroup>
          </div>
        </div>
        <DialogFooter>
          <ButtonGroup direction={'rtl'}>
            <Button type={'submit'} variant={'cta'} className={'confirm'}>
              確定
            </Button>
            <Button variant={'primary'} className={'close'} onClick={onClose}>
              取消
            </Button>
          </ButtonGroup>
        </DialogFooter>
      </form>
      <DevTool control={control}/>
    </Dialog>
  );
}

export default ShiftScheduleForm;
